//  @flow
import React, { Component, Fragment } from 'react';
import { membersHead, membersPE, membersCO, social } from '../about-us.data';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import environment from '../../../environment';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import WeAre from './WeAre/WeAre';
import CarouselPressNew from '../../home/components/CarouselPressNew/CarouselPressNew';
import classNames from 'classnames/bind';

import coverImg from '../../../assets/img/meta-cover-lightblue.png';
import style from './AboutUs.scss';
import icons from '../../../assets/styles/icons.scss';
import family from '../../../assets/img/about-us/family.png';
import peru from '../../../assets/img/about-us/peru.png';
import colombia from '../../../assets/img/about-us/colombia.png';
import miami from '../../../assets/img/about-us/miami.png';
import venturecity from '../../../assets/img/about-us/venturecity-logo.png';
import blackbox from '../../../assets/img/about-us/blackbox-logo.png';
import mit from '../../../assets/img/about-us/mit-logo.png';


import type { Member } from '../about-us.data';
import type { Locale, LocaleState } from '../../locale/duck';
import type { Country } from '../../../entities';
import type { LocalValuesState } from '../../local-values/duck';

type Props = {
  +locale: LocaleState;
  +localValues: LocalValuesState;
};

const messages = defineMessages({
  US: {
    title: {
      id: 'aboutUs.title.US',
    },
    description: {
      id: 'aboutUs.description.US',
    },
    titleOg: {
      id: 'aboutUs.title.og.US',
    },
    descriptionOg: {
      id: 'aboutUs.description.og.US',
    },
  },
  PE: {
    title: {
      id: 'aboutUs.title.PE',
    },
    description: {
      id: 'aboutUs.description.PE',
    },
    titleOg: {
      id: 'aboutUs.title.og.PE',
    },
    descriptionOg: {
      id: 'aboutUs.description.og.PE',
    },
  },
  CO: {
    title: {
      id: 'aboutUs.title.CO',
    },
    description: {
      id: 'aboutUs.description.CO',
    },
    titleOg: {
      id: 'aboutUs.title.og.CO',
    },
    descriptionOg: {
      id: 'aboutUs.description.og.CO',
    },
  },
  default: {
    title: {
      id: 'aboutUs.title',
    },
    description: {
      id: 'aboutUs.description',
    },
    titleOg: {
      id: 'aboutUs.title.og',
    },
    descriptionOg: {
      id: 'aboutUs.description.og',
    },
  },
});

const storyPE_ES = <Fragment>
  <p>Qempo nació en el 2016 para simplificar las compras en el mundo y así crear oportunidades a miles de personas.</p>
  <p>Todo inició en el 2008, cuando dos amigos de la escuela se unieron para ayudar a sus familiares y amigos a poder
    comprar en Estados Unidos. El servicio se inició con el nombre de AnsonImport, mientras Anson y Mauricio trabajaban
    desde casa a los 17 años.</p>
  <p>Años después, decidieron desarrollar Qempo como plataforma para integrar tecnología al servicio que ya venían
    ofreciendo a miles de amigos y clientes.</p>
  <p>Para poder lograrlo, se juntaron con amigos que confiaron en la misión de eliminar barreras en el mundo y así
    formaron la familia que hoy es Qempo.</p>
</Fragment>;

const storyPE_EN = <Fragment>
  <p>Qempo was founded in 2016 to simplify the shopping experience around the world and generate new opportunities.</p>
  <p>Everything started in 2008, when two friends from school got together to help their friends and family to be able
    to buy from the United States. The service started under the name of AnsonImport, while Anson and Mauricio worked
    from their homes at the age of 17.</p>
  <p>Years later, they decided to develop Qempo as a platform to integrate technology to the service that they were
    already providing to thousands of friends and customers.</p>
  <p>To accomplish it, they got together with friends who also trusted their mission to eliminate barriers around the
    world and formed what is today, the Qempo family.</p>
</Fragment>;

const storyCO_ES = <Fragment>
  <p>Qempo nació en el 2016 para simplificar las compras en el mundo y así crear oportunidades a miles de personas.</p>
  <p>Todo inició en el 2008, cuando dos amigos de la escuela se unieron para ayudar a sus familiares y amigos a poder
    comprar en Estados Unidos. El servicio se inició con el nombre de AnsonImport, mientras Anson y Mauricio trabajaban
    desde casa a los 17 años.</p>
  <p>Años después, con la ayuda de Andres para el crecimiento en el mercado colombiano, decidieron desarrollar Qempo
    como plataforma para integrar tecnología al servicio que ya venían ofreciendo a miles de amigos y clientes.</p>
  <p>Para poder lograrlo, se juntaron con amigos que confiaron en la misión de eliminar barreras en el mundo y así
    formaron la familia que hoy es Qempo.</p>
</Fragment>;

const storyCO_EN = <Fragment>
  <p>Qempo was founded in 2016 to simplify the shopping experience around the world and generate new opportunities.</p>
  <p>Everything started in 2008, when two friends from school got together to help their friends and family to be able
    to buy from the United States. The service started under the name of AnsonImport, while Anson and Mauricio worked
    from their homes at the age of 17.</p>
  <p>Years later, with the help of Andres to grow in the colombian market, they decided to develop Qempo as a platform
    to integrate technology to the service that they were already providing to thousands of friends and customers.</p>
  <p>To accomplish it, they got together with friends who also trusted their mission to eliminate barriers around the
    world and formed what is today, the Qempo family.</p>
</Fragment>;

const mentors = [
  {
    name: 'The Venture City',
    url: 'https://medium.com/theventurecity/qempo-the-best-products-without-borders-e3552e453cb5',
    image: venturecity,
  },
  {
    name: 'Blackbox',
    url: 'https://medium.com/@blackboxvc/fourteen-founders-from-13-countries-selected-to-join-blackbox-connect-23-8b1dfc6313f6',
    image: blackbox,
  },
  {
    name: 'MIT Technology Review',
    url: 'https://www.innovatorsunder35.com/the-list/anson-tou/',
    image: mit,
  },
];

const places = [
  { place: 'Perú', image: peru, address: 'Jr. Trujillo 427, Piso 2, Magdalena del Mar, Lima, Perú' },
  { place: 'Colombia', image: colombia, address: 'Edificio Plaza del Poblado Calle 10 # 42-67 Oficina 418' },
  { place: 'Miami', image: miami, address: '14945 SW 34th ST, Miami FL 33185, Florida, United States' },
];

export default class AboutUs extends Component<Props, void> {
  getMembers(domainCountry: Country) {
    if (domainCountry === 'CO') {
      return [...membersHead, ...membersCO, ...membersPE];
    } else {
      return [...membersHead, ...membersPE, ...membersCO];
    }
  }

  getStoryContent(domainCountry: Country, locale: Locale) {
    if (domainCountry === 'CO') {
      if (locale === 'es') {
        return storyCO_ES;
      } else if (locale === 'en') {
        return storyCO_EN;
      }
    }

    if (locale === 'es') {
      return storyPE_ES;
    } else if (locale === 'en') {
      return storyPE_EN;
    }
    // Default PE ES
    return storyPE_ES;
  }

  render() {
    const { locale, localValues } = this.props;
    const members = this.getMembers(locale.domainCountry);
    const membersElem = members.map((m: Member, index: number) =>
      (<article className={style.teamMate} key={index}>
        <picture>
          <img src={m.image} alt={m.fullName}/>
        </picture>
        <div className={style.content}>
          <h4 className={style.memberName}>{m.fullName}</h4>
          <p className={style.memberTitle}>{m.title}</p>
          {m.words.map((w: string, idx: number) => <p className={style.memberWord}
                                                      key={`p-${idx}`}>{w}</p>)}
          <ul className={style.memberLinks}>
            {Object.keys(m.links).map((k: string, index: number) => {
              let image;
              switch (k) {
                case 'twitter':
                  image = social.twitter;
                  break;
                case 'linkedin':
                  image = social.linkedin;
                  break;
                case 'github':
                  image = social.github;
                  break;
                case 'url':
                  image = social.url;
                  break;
              }
              return (
                <li key={index}>
                  <a href={m.links[k]}
                     target="_blank">
                    <picture key={index}>
                      <img src={image} alt={k}/>
                    </picture>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </article>)
    );
    const mentorsElem = mentors.map((m: { [key: string]: string }, index: number) =>
      <a href={m.url}
         target="_blank"
         className={style.bottomBlockBox} key={index}>
        <picture>
          <img src={m.image} alt={m.name}/>
        </picture>
      </a>);
    const placesElem = places.map((p: { [key: string]: string }, index: number) =>
      <article className={style.bottomBlockBox} key={index}>
        <picture>
          <img src={p.image} alt={p.place}/>
        </picture>
        <h3>{p.place}</h3>
        <div className={style.content}>
          <h4>{p.place}</h4>
          <p>{p.address}</p>
        </div>
      </article>);
    const description = this.context.intl.formatMessage((messages[locale.domainCountry] || messages.default).description);
    const descriptionOg = this.context.intl.formatMessage((messages[locale.domainCountry] || messages.default).descriptionOg);
    const title = this.context.intl.formatMessage((messages[locale.domainCountry] || messages.default).title);
    const titleOg = this.context.intl.formatMessage((messages[locale.domainCountry] || messages.default).titleOg);
    const baseUrl = environment.baseUrl[locale.domainCountry || 'US'];
    return <Fragment>
      <Helmet meta={[
        { name: 'description', content: description },
        { property: 'og:image', content: `${baseUrl}${coverImg}` },
        { property: 'og:title', content: titleOg },
        { property: 'og:description', content: descriptionOg },
        { name: 'twitter:title', content: title },
        { name: 'twitter:twitter:image', content: `${baseUrl}${coverImg}` },
        { name: 'twitter:twitter:description', content: description },
      ]}>
        <title>{title}</title>
      </Helmet>
      <section className={style.mainWrap}>
        <FormattedMessage id="aboutUs.mainText">{
          txt => <h1 className={style.mainText}>{txt}</h1>
        }</FormattedMessage>
        <i className={classNames(icons.iconChevronDownLight, style.icon)}/>
        <FormattedMessage id="aboutUs.ourStory">{
          txt => <h2 className={style.ourStory}>{txt}</h2>
        }</FormattedMessage>

        <article className={style.story}>
          {this.getStoryContent(locale.domainCountry, locale.locale)}
        </article>

        <img src={family}
             className={style.family}
             alt="Qempo Family"/>

        <FormattedMessage id="aboutUs.weAre.title">{
          txt => <h2 className={style.weAreTitle}>{txt}<WeAre/></h2>
        }</FormattedMessage>
        <FormattedMessage id="aboutUs.weAre.text">{
          txt => <p className={style.weAreText}>{txt}</p>
        }</FormattedMessage>
      </section>
      <section className={style.memberWrap}>
        {membersElem}
      </section>
      <section className={style.bottomBlock}>
        <FormattedMessage id="aboutUs.believe.fearless">{
          text => <h2>{text}</h2>
        }</FormattedMessage>
        {mentorsElem}
      </section>
      <section className={style.bottomBlock}>
        <FormattedMessage id="aboutUs.qempoWorld.title">{
          text => <h2>{text}</h2>
        }</FormattedMessage>
        {placesElem}
        <br/><br/><br/><br/>
        <CarouselPressNew locale={locale}/>
      </section>
      <br/><br/><br/><br/><br/>
    </Fragment>;
  }
}

AboutUs.propTypes = {
  locale: PropTypes.object,
  localValues: PropTypes.object,
};

AboutUs.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/about-us/components/AboutUs.jsx